import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Link } from 'types/api.types';
import {
  CurrencyConfiguration,
  Subscription,
  Tier,
  SubscriptionQuota,
  ActiveQuotaRateStats,
} from 'types/subscription.types';
import { DDL } from 'types/general.types';
import { LoadingState } from 'types/enums';
import {
  checkSubscriptionEffect,
  fetchSubscriptionQuotasEffect,
  fetchSubscriptionTierListEffect,
  fetchSubscriptionTiersDDLEffect,
} from './effects';

export interface SubscriptionState {
  tierList: Tier[];
  currencyConfig: CurrencyConfiguration | null;
  subscription: Subscription | null;
  subscriptionQuota: SubscriptionQuota | null;
  activeQuotaRateStats: ActiveQuotaRateStats;
  subscriptionTiersDDL?: DDL;
  tiersDDLLoading: LoadingState;
  tierListLoading: LoadingState;
  subscriptionsLoading: LoadingState;
  _links?: {
    self?: Link;
    getSignedCheckoutLink?: Link;
  };
}

export const subscriptionInitialState: SubscriptionState = {
  tierList: [],
  subscription: null,
  currencyConfig: null,
  subscriptionQuota: null,
  tierListLoading: LoadingState.PRISTINE,
  tiersDDLLoading: LoadingState.PRISTINE,
  subscriptionsLoading: LoadingState.PRISTINE,
  activeQuotaRateStats: {
    lyricsCowriterRequestRateLimit: null,
  },
};

const slice = createSlice({
  name: 'subscription',
  initialState: subscriptionInitialState,
  reducers: {
    setLyricsCowriterRequestRateLimit(
      state,
      {
        payload: { iso, reset },
      }: PayloadAction<{ reset: boolean; iso: string }>
    ) {
      if (
        !state.subscriptionQuota ||
        !state.activeQuotaRateStats.lyricsCowriterRequestRateLimit
      )
        return;

      const date = new Date(iso);
      const timestamp = date.getTime() / 1000;

      const remaining = reset
        ? state.subscriptionQuota.default.lyricsCowriterRequestRateLimit.limit
        : 0;

      state.activeQuotaRateStats.lyricsCowriterRequestRateLimit = {
        remaining,
        resetAt: { iso, timestamp },
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSubscriptionTierListEffect.pending, (state) => {
        state.tierListLoading = LoadingState.FETCHING;
      })
      .addCase(
        fetchSubscriptionTierListEffect.fulfilled,
        (state, { payload: { currencyConfiguration, subscriptionTiers } }) => {
          state.tierListLoading = LoadingState.FETCHED;
          state.tierList = subscriptionTiers.items;
          state.currencyConfig = currencyConfiguration.item;
          state._links = subscriptionTiers._links;
        }
      )
      .addCase(fetchSubscriptionTierListEffect.rejected, (state) => {
        state.tierListLoading = LoadingState.ERROR;
      })
      .addCase(checkSubscriptionEffect.pending, (state) => {
        state.subscriptionsLoading = LoadingState.FETCHING;
      })
      .addCase(checkSubscriptionEffect.fulfilled, (state, { payload }) => {
        state.subscriptionsLoading = LoadingState.FETCHED;
        state.subscription = payload;
      })
      .addCase(checkSubscriptionEffect.rejected, (state) => {
        state.subscriptionsLoading = LoadingState.ERROR;
      })
      .addCase(fetchSubscriptionTiersDDLEffect.pending, (state) => {
        state.tiersDDLLoading = LoadingState.FETCHING;
      })
      .addCase(
        fetchSubscriptionTiersDDLEffect.fulfilled,
        (state, { payload }) => {
          state.tiersDDLLoading = LoadingState.FETCHED;
          state.subscriptionTiersDDL = payload.tiers;
        }
      )
      .addCase(fetchSubscriptionTiersDDLEffect.rejected, (state) => {
        state.tiersDDLLoading = LoadingState.ERROR;
      })
      .addCase(
        fetchSubscriptionQuotasEffect.fulfilled,
        (state, { payload }) => {
          state.subscriptionQuota = payload.quota.item;
          state.activeQuotaRateStats = payload.activeQuotaRateStats.item;
        }
      );
  },
});

export const { setLyricsCowriterRequestRateLimit } = slice.actions;

export const subscriptionReducer = slice.reducer;
